import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));

  // Try to refresh the access token using refresh token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = localStorage.getItem('admin_refresh_token');
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await axios.post(`${API}/api/admin/auth/refresh`, {
        refreshToken
      });
      
      const { token: newToken } = response.data;
      localStorage.setItem('admin_token', newToken);
      setToken(newToken);
      return newToken;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return null;
    }
  }, []);

  const verifyToken = useCallback(async () => {
    const currentToken = localStorage.getItem('admin_token');
    if (!currentToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/api/admin/auth/me`, {
        headers: { Authorization: `Bearer ${currentToken}` }
      });
      setAdmin(response.data);
      setToken(currentToken);
    } catch (error) {
      console.error('Token verification failed:', error?.response?.status);
      
      // If 401, try to refresh the token
      if (error?.response?.status === 401) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          // Retry with new token
          try {
            const retryResponse = await axios.get(`${API}/api/admin/auth/me`, {
              headers: { Authorization: `Bearer ${newToken}` }
            });
            setAdmin(retryResponse.data);
            return;
          } catch (retryError) {
            console.error('Retry after refresh failed:', retryError);
          }
        }
      }
      
      // All attempts failed, logout
      logout();
    } finally {
      setLoading(false);
    }
  }, [refreshAccessToken]);

  useEffect(() => {
    verifyToken();
  }, [verifyToken]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/api/admin/auth/login`, {
        email,
        password
      });
      
      const { token: newToken, refreshToken, admin: adminData } = response.data;
      
      localStorage.setItem('admin_token', newToken);
      localStorage.setItem('admin_refresh_token', refreshToken);
      setToken(newToken);
      setAdmin(adminData);
      
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.error || 'Błąd logowania' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    setToken(null);
    setAdmin(null);
  };

  const hasPermission = (permission) => {
    if (!admin) return false;
    if (admin.permissions?.includes('*')) return true;
    return admin.permissions?.includes(permission) || false;
  };

  const getAuthHeader = () => {
    // Always get fresh token from localStorage as fallback for Safari/Firefox
    const currentToken = token || localStorage.getItem('admin_token');
    return {
      Authorization: `Bearer ${currentToken}`
    };
  };

  return (
    <AdminAuthContext.Provider value={{
      admin,
      loading,
      isAuthenticated: !!admin,
      login,
      logout,
      hasPermission,
      getAuthHeader,
      token
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export default AdminAuthContext;
